import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import {
  Tooltip,
  TooltipProps,
  UnstyledButton,
  UnstyledButtonProps,
} from '@mantine/core';
import { clsx } from 'clsx';
import { forwardRef } from 'react';
import styles from './ButtonLink.module.scss';
/**
 * Props for the ButtonLink component
 * @interface ButtonLinkProps
 */
export interface ButtonLinkProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, 'style' | 'type' | 'ref' | 'size'>,
    Omit<UnstyledButtonProps, 'style' | 'classNames'> {
  /**
   * Can be an array of single element or multiple elements for multiline tooltips.
   */
  tooltipLabel?: string[];
  /** Whether to disable the tooltip display */
  isTooltipDisabled?: boolean;
  /** Position of the tooltip relative to the element */
  tooltipPosition?: TooltipProps['position'];
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
}

/**
 * ButtonLink is an accessible button component that provides a link-like appearance with button semantics.
 * It supports tooltips, different variants, and sizes while maintaining proper accessibility.
 *
 * @component
 * @example
 * // Primary small button with tooltip
 * <ButtonLink
 *   tooltipLabel={["Add Item"]}
 *   variant="primary"
 *   size="small"
 *   onClick={handleAdd}
 * >
 *   Add
 * </ButtonLink>
 *
 * @example
 * // Secondary medium button with multiline tooltip
 * <ButtonLink
 *   tooltipLabel={["Delete Item", "This action cannot be undone"]}
 *   variant="secondary"
 *   size="medium"
 *   onClick={handleDelete}
 * >
 *   Delete
 * </ButtonLink>
 *
 * @example
 * // Disabled button with tooltip
 * <ButtonLink
 *   tooltipLabel={["Not Available"]}
 *   disabled={true}
 *   variant="primary"
 * >
 *   Submit
 * </ButtonLink>
 *
 * Key Features:
 * - Two visual variants: primary and secondary
 * - Two size options: small and medium
 * - Built-in tooltip support with single/multiline options
 * - Proper accessibility attributes and keyboard navigation
 * - Consistent disabled state handling
 * - Forward ref support for external ref handling
 *
 * @param {ButtonLinkProps} props - Component props
 * @returns {JSX.Element} Rendered button component with link-like styling
 */
export const ButtonLink = forwardRef<HTMLButtonElement, ButtonLinkProps>(
  (
    {
      children,
      className,
      tooltipLabel,
      isTooltipDisabled,
      tooltipPosition,
      disabled,
      variant = 'primary',
      size = 'small',
      ...rest
    },
    ref,
  ) => {
    const parsedTooltipLabel = tooltipLabel?.join('\n') ?? '';
    const titleLabel = tooltipLabel?.[0] ?? '';

    return (
      <Tooltip
        label={parsedTooltipLabel}
        position={tooltipPosition}
        disabled={isTooltipDisabled || !parsedTooltipLabel}
        multiline={tooltipLabel && tooltipLabel.length > 1}
        classNames={{ tooltip: styles.multilineTooltip }}
      >
        <UnstyledButton
          ref={ref}
          aria-disabled={disabled}
          aria-label={titleLabel}
          className={clsx(styles.clickableRegionButton, className)}
          disabled={disabled}
          type="button"
          {...rest}
        >
          <TextComponent
            component="span"
            variant={size === 'small' ? 'bodySmall' : 'bodyMedium'}
            color={variant === 'primary' ? 'primary' : 'base'}
            hoverColor={variant === 'primary' ? 'primary' : 'white'}
            className={styles.text}
          >
            {children}
          </TextComponent>
        </UnstyledButton>
      </Tooltip>
    );
  },
);
